import { useState } from 'react';
import { AlertCircle, CheckCircle2, Calendar, Users, Bell, ExternalLink, Clock, Globe } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function GoogleCalendarIntegration() {
  const [isConnected, setIsConnected] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [redirectUri, setRedirectUri] = useState('');

  const handleConnect = (event: React.FormEvent) => {
    event.preventDefault();
    if (clientId && clientSecret && redirectUri) {
      setIsConnected(true);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <div className="flex-1 p-8">
        <header className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-gray-900">Google Calendar Integration</h1>
          <p className="text-gray-600">
            Connect your Google Calendar to synchronize events and enhance your scheduling capabilities.
          </p>
        </header>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <Card className="col-span-2">
            <CardHeader>
              <CardTitle>Connect to Google Calendar</CardTitle>
              <CardDescription>Enter your Google Calendar API credentials to establish the connection.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleConnect} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="client-id">Client ID</Label>
                  <Input
                    id="client-id"
                    type="text"
                    placeholder="Enter your Google Calendar Client ID"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client-secret">Client Secret</Label>
                  <Input
                    id="client-secret"
                    type="password"
                    placeholder="Enter your Google Calendar Client Secret"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="redirect-uri">Redirect URI</Label>
                  <Input
                    id="redirect-uri"
                    type="url"
                    placeholder="https://your-app.com/auth/google/callback"
                    value={redirectUri}
                    onChange={(e) => setRedirectUri(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="scopes">API Scopes</Label>
                  <Select defaultValue="read_write">
                    <SelectTrigger id="scopes">
                      <SelectValue placeholder="Select API scopes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="read_only">Read Only</SelectItem>
                      <SelectItem value="read_write">Read and Write</SelectItem>
                      <SelectItem value="full_access">Full Access</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full">
                  {isConnected ? 'Connected' : 'Connect'} <Calendar className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </CardContent>
            <CardFooter className="justify-between">
              <p className="text-sm text-gray-500">Need help with OAuth 2.0?</p>
              <Button variant="outline">View Google Calendar API Guide</Button>
            </CardFooter>
          </Card>

          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>Connection Status</CardTitle>
              </CardHeader>
              <CardContent>
                {isConnected ? (
                  <div className="flex items-center text-green-600">
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Connected
                  </div>
                ) : (
                  <div className="flex items-center text-yellow-600">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    Not Connected
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Integration Features</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-4">
                  <li className="flex items-center justify-between">
                    <span className="flex items-center">
                      <Clock className="w-4 h-4 mr-2" />
                      Event Sync
                    </span>
                    <Switch />
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="flex items-center">
                      <Users className="w-4 h-4 mr-2" />
                      Attendee Management
                    </span>
                    <Switch />
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="flex items-center">
                      <Globe className="w-4 h-4 mr-2" />
                      Time Zone Support
                    </span>
                    <Switch />
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="flex items-center">
                      <Bell className="w-4 h-4 mr-2" />
                      Notifications
                    </span>
                    <Switch />
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="mt-12">
          <h2 className="mb-4 text-2xl font-semibold">How It Works</h2>
          <Tabs defaultValue="connect" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="connect">Connect</TabsTrigger>
              <TabsTrigger value="sync">Sync</TabsTrigger>
              <TabsTrigger value="manage">Manage</TabsTrigger>
            </TabsList>
            <TabsContent value="connect" className="p-4 bg-white rounded-b-lg shadow">
              <h3 className="mb-2 font-semibold">1. Connect Your Google Calendar</h3>
              <p>
                Enter your Google Calendar API credentials and authorize the integration to establish a secure
                connection.
              </p>
            </TabsContent>
            <TabsContent value="sync" className="p-4 bg-white rounded-b-lg shadow">
              <h3 className="mb-2 font-semibold">2. Synchronize Your Events</h3>
              <p>
                Choose which calendars and event types you want to sync between Google Calendar and your application.
              </p>
            </TabsContent>
            <TabsContent value="manage" className="p-4 bg-white rounded-b-lg shadow">
              <h3 className="mb-2 font-semibold">3. Manage Events</h3>
              <p>
                Create, update, and delete events in your application, with changes automatically reflected in Google
                Calendar.
              </p>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <aside className="hidden w-64 p-6 bg-white border-l border-gray-200 lg:block">
        <h2 className="mb-4 text-lg font-semibold">Quick Links</h2>
        <nav className="space-y-2">
          <a href="#" className="flex items-center text-blue-600 hover:underline">
            Google Calendar Dashboard
            <ExternalLink className="w-4 h-4 ml-1" />
          </a>
          <a href="#" className="flex items-center text-blue-600 hover:underline">
            Manage Calendars
            <ExternalLink className="w-4 h-4 ml-1" />
          </a>
          <a href="#" className="flex items-center text-blue-600 hover:underline">
            Event Settings
            <ExternalLink className="w-4 h-4 ml-1" />
          </a>
        </nav>
        <div className="mt-8">
          <Badge variant="outline" className="mb-2">
            Pro Tip
          </Badge>
          <p className="text-sm text-gray-600">
            Use Google Calendar's color coding feature to categorize and visually organize your events.
          </p>
        </div>
      </aside>
    </div>
  );
}
